import React, { useState, useEffect, useCallback } from 'react';
import { doc, updateDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { askGPT } from '../components/OpenAIService';
import ReactMarkdown from 'react-markdown';
import { 
  Compass, Lightbulb, Target, Zap, Edit3, RefreshCw, X, ArrowRight,
  ArrowLeft, AlertCircle, Loader2, CheckSquare, Clock, List, Edit, 
  Save, Plus, Trash2, MessageCircle, Search, BookOpen, GraduationCap
} from 'lucide-react';

// ============================================================================
// COMPOSANT STRATÉGIE - VERSION CORRIGÉE AVEC MISES À JOUR BDD
// ============================================================================

const StrategyStep = React.memo(({ currentCompetence, userId, onNavigateToAction, onUpdateObjective }) => {
  const [recommendation, setRecommendation] = useState(currentCompetence?.recommendation || '');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [tempObjective, setTempObjective] = useState(currentCompetence?.objectifOriginal || currentCompetence?.title || '');
  const [error, setError] = useState(null);
  
  // État local pour forcer le re-render après mise à jour
  const [localCompetence, setLocalCompetence] = useState(currentCompetence);

  // Mettre à jour la recommandation et l'objectif local quand currentCompetence change
  useEffect(() => {
    if (currentCompetence) {
      setLocalCompetence(currentCompetence);
      if (currentCompetence?.recommendation !== recommendation) {
        setRecommendation(currentCompetence?.recommendation || '');
      }
      // Synchroniser tempObjective avec l'objectif original
      setTempObjective(currentCompetence?.objectifOriginal || currentCompetence?.title || '');
    }
  }, [currentCompetence]);

  // ============================================================================
  // FONCTION DE MISE À JOUR COMPLÈTE AVEC BDD - INSPIRÉE D'OBJECTIVEMODULE
  // ============================================================================
  
  const processObjectiveUpdateWithAI = async (newObjectiveText) => {
    if (!newObjectiveText.trim() || !localCompetence?.id) return;
    
    try {
      setIsGenerating(true);
      
      // 1. Régénération du titre et domaine avec l'IA (comme dans ObjectiveModule)
      const regenerationPrompt = `En tant qu'expert en développement personnel et professionnel, analyse cet objectif utilisateur et génère une structure mise à jour.

Objectif utilisateur modifié : "${newObjectiveText.trim()}"

Génère une réponse au format JSON strict avec cette structure :
{
  "title": "Titre concis et motivant de l'objectif (max 80 caractères)",
  "chapitre": "Domaine principal parmi : Professionnel, Personnel, Educatif, Business, Activités ludique et créative, Développement entrepreneurial, Formation et apprentissage, Évolution de carrière, Santé et bien-être, Objectifs Personnels, Voyages, Gestion immobilière, Finance et Banque, Activités sportives"
}

Assure-toi que :
- Le titre soit accrocheur et motivant
- Le domaine corresponde exactement à un des domaines listés`;

      const regenerationResult = await askGPT(regenerationPrompt);
      
      // Parser la réponse JSON
      let updatedData = {
        title: newObjectiveText.trim(),
        chapitre: localCompetence.chapitre || 'Personnel'
      };
      
      try {
        const jsonMatch = regenerationResult.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]);
          updatedData = {
            title: parsed.title || newObjectiveText.trim(),
            chapitre: parsed.chapitre || localCompetence.chapitre || 'Personnel'
          };
        }
      } catch (parseError) {
        console.error('Erreur parsing JSON régénération:', parseError);
      }

      // 2. Génération de la nouvelle stratégie
      const strategyPrompt = `Tu es un coach, conseiller spécialisé en développement personnel, éducatif et professionnel.
                     Ton rôle est d'informer l'étudiant et l'aider à réaliser son projet. 
                     Adresse toi directement à l'étudiant. 

                     Format de réponse obligatoire avec balises [SECTION1], [SECTION2], [SECTION3] :

                     [SECTION1]
                     {Présente le sujet et le contexte à l'étudiant, si l'étudiant à posé une question répond à sa question, sans titre ni introduction. En 100 mots max}

                     [SECTION2]
                     {Propose une démarche pour réaliser cette action, ou cet objectif. Préciser les questions ou les orientations qui restent à définir par l'utilisateur. Si le sujet n'est pas suffisament explicite, pose une ou deux questions pour clarifier le sens et l'objectif de l'étudiant, sans titre ni introduction. en 100 mots max. }

                     [SECTION3] 
                     {Formule tes recommandations et propositions et invite l'étudiant à poursuivre dans l'espace inspiration, sans titre ni introduction, en 70 mots max}

                     Important :
                     - Utilise le markdown pour le formatage
                     - Mets les mots clés en **gras** ou *italique*
                     - Utilise $...$ pour les formules en ligne, $$...$$ pour les formules affichées

                     Voici l'objectif de l'étudiant : "${newObjectiveText.trim()}"`;

      const newRecommendation = await askGPT(strategyPrompt);

      // 3. Génération d'un nouveau plan d'action basé sur la stratégie mise à jour
      const actionPlanPrompt = `En tant que coach spécialisé en développement personnel et professionnel, crée un plan d'action concret basé sur l'objectif mis à jour.

Objectif de l'utilisateur : "${newObjectiveText.trim()}"
Nouvelle stratégie : "${newRecommendation}"

Génère un plan d'action avec 3 à 5 étapes claires et actionnables sous format JSON avec cette structure précise :
{
  "titre": "Plan d'action pour [résumé de l'objectif]",
  "description": "Une description brève du plan d'action qui explique comment il va aider l'utilisateur à atteindre son objectif",
  "steps": [
    {
      "id": 1,
      "titre": "Titre de l'étape 1",
      "description": "Description détaillée de l'étape 1 avec des actions concrètes",
      "duree": "Estimation de la durée en heures.",
      "termine": false
    }
  ]
}

Assure-toi que:
- Chaque étape soit spécifique, mesurable et réalisable
- Le format JSON soit strictement respecté pour faciliter le traitement
- Le contenu utilise des verbes d'action et encourage l'utilisateur
- Les étapes suivent une progression logique vers l'objectif final`;

      const newActionPlanResult = await askGPT(actionPlanPrompt);
      
      // Parser le nouveau plan d'action
      let newActionPlan = null;
      try {
        const actionJsonMatch = newActionPlanResult.match(/\{[\s\S]*\}/);
        if (actionJsonMatch) {
          newActionPlan = JSON.parse(actionJsonMatch[0]);
        }
      } catch (parseError) {
        console.error('Erreur parsing JSON plan d\'action:', parseError);
      }
      
      // 4. MISE À JOUR EN BASE DE DONNÉES (point clé manquant dans la version précédente)
      const updateData = {
        objectifOriginal: newObjectiveText.trim(),
        title: updatedData.title,
        chapitre: updatedData.chapitre,
        recommendation: newRecommendation,
        recommendationUpdatedAt: new Date().toISOString(),
        updatedAt: serverTimestamp(),
        lastAIUpdate: new Date().toISOString()
      };

      // Ajouter le nouveau plan d'action si généré avec succès
      if (newActionPlan) {
        updateData.actionPlan = newActionPlan;
        updateData.actionPlanUpdatedAt = new Date().toISOString();
      }

      // MISE À JOUR FIREBASE
      await updateDoc(doc(db, 'users', userId, 'competences', localCompetence.id), updateData);
      
      // 5. Mettre à jour l'état local immédiatement avec les mêmes données
      const updatedCompetence = {
        ...localCompetence,
        objectifOriginal: newObjectiveText.trim(),
        title: updatedData.title,
        chapitre: updatedData.chapitre,
        recommendation: newRecommendation,
        recommendationUpdatedAt: new Date().toISOString(),
        lastAIUpdate: new Date().toISOString(),
        ...(newActionPlan && {
          actionPlan: newActionPlan,
          actionPlanUpdatedAt: new Date().toISOString()
        })
      };
      
      setLocalCompetence(updatedCompetence);
      setRecommendation(newRecommendation);
      setIsEditing(false);
      
      // 6. Notifier le composant parent de la mise à jour (comme dans ObjectiveModule)
      if (onUpdateObjective) {
        onUpdateObjective(updatedCompetence);
      }
      
    } catch (error) {
      console.error('Erreur lors de la mise à jour complète:', error);
      setError('Erreur lors de la sauvegarde et régénération.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Générer les recommandations (fonction simplifiée car maintenant intégrée dans processObjectiveUpdateWithAI)
  const generateRecommendations = useCallback(async (objective) => {
    if (!objective || !userId || !localCompetence?.id) return;
    
    setIsGenerating(true);
    setError(null);
    
    try {
      const prompt = `Tu es un coach, conseiller spécialisé en développement personnel, éducatif et professionnel.
                     Ton rôle est d'informer l'étudiant et l'aider à réaliser son projet. 
                     Adresse toi directement à l'étudiant. 

                     Format de réponse obligatoire avec balises [SECTION1], [SECTION2], [SECTION3] :

                     [SECTION1]
                     {Présente le sujet et le contexte à l'étudiant, si l'étudiant à posé une question répond à sa question, sans titre ni introduction. En 100 mots max}

                     [SECTION2]
                     {Propose une démarche pour réaliser cette action, ou cet objectif. Préciser les questions ou les orientations qui restent à définir par l'utilisateur. Si le sujet n'est pas suffisament explicite, pose une ou deux questions pour clarifier le sens et l'objectif de l'étudiant, sans titre ni introduction. en 100 mots max. }

                     [SECTION3] 
                     {Formule tes recommandations et propositions et invite l'étudiant à poursuivre dans l'espace inspiration, sans titre ni introduction, en 70 mots max}

                     Important :
                     - Utilise le markdown pour le formatage
                     - Mets les mots clés en **gras** ou *italique*
                     - Utilise $...$ pour les formules en ligne, $$...$$ pour les formules affichées

                     Voici l'objectif de l'étudiant : "${objective}"`;

      const result = await askGPT(prompt);
      
      if (result) {
        // Sauvegarder SEULEMENT la recommandation en Firestore
        await updateDoc(doc(db, 'users', userId, 'competences', localCompetence.id), {
          recommendation: result,
          recommendationUpdatedAt: new Date().toISOString()
        });
        
        setRecommendation(result);
        // Mettre à jour l'état local
        setLocalCompetence(prev => ({
          ...prev,
          recommendation: result,
          recommendationUpdatedAt: new Date().toISOString()
        }));

        // Notifier le parent
        if (onUpdateObjective) {
          onUpdateObjective({
            ...localCompetence,
            recommendation: result,
            recommendationUpdatedAt: new Date().toISOString()
          });
        }
      }
    } catch (error) {
      console.error('Erreur de génération:', error);
      setError('Erreur lors de la génération des recommandations.');
    } finally {
      setIsGenerating(false);
    }
  }, [userId, localCompetence, onUpdateObjective]);

  // Handler pour sauvegarder l'objectif modifié avec régénération complète
  const handleSaveObjective = async () => {
    if (!tempObjective.trim() || !localCompetence?.id) return;
    
    await processObjectiveUpdateWithAI(tempObjective.trim());
  };

  // Formater le contenu des recommandations
  const formatRecommendationContent = (content) => {
    if (!content) return null;

    const sections = {
      section1: '',
      section2: '',
      section3: ''
    };

    // Extraire les sections
    const section1Match = content.match(/\[SECTION1\](.*?)(?=\[SECTION2\]|$)/s);
    const section2Match = content.match(/\[SECTION2\](.*?)(?=\[SECTION3\]|$)/s);
    const section3Match = content.match(/\[SECTION3\](.*?)$/s);

    if (section1Match) sections.section1 = section1Match[1].trim();
    if (section2Match) sections.section2 = section2Match[1].trim();
    if (section3Match) sections.section3 = section3Match[1].trim();

    return sections;
  };

  const sections = formatRecommendationContent(recommendation);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* En-tête avec objectif - HARMONISÉ */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-4">
          <div className="flex items-center gap-3">
            <Compass className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 break-words">Votre objectif</h3>
              <p className="text-xs sm:text-sm text-gray-600 break-words">Recommandations personnalisées</p>
            </div>
          </div>
          
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="p-2 hover:bg-blue-100 rounded-full transition-colors"
              title={isEditing ? "Annuler" : "Modifier l'objectif"}
              disabled={isGenerating}
            >
              {isEditing ? 
                <X className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" /> : 
                <Edit3 className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
              }
            </button>
            
            <button
              onClick={() => generateRecommendations(localCompetence.objectifOriginal || localCompetence.title)}
              disabled={isGenerating}
              className="p-2 hover:bg-green-100 rounded-full transition-colors disabled:opacity-50"
              title="Régénérer les recommandations"
            >
              <RefreshCw className={`w-4 h-4 sm:w-5 sm:h-5 text-green-600 ${isGenerating ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Zone d'édition ou affichage de l'objectif - AMÉLIORÉ */}
        {isEditing ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Texte original de votre objectif :
              </label>
              <textarea
                value={tempObjective}
                onChange={(e) => setTempObjective(e.target.value)}
                className="w-full p-3 sm:p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base sm:text-lg bg-white resize-none"
                rows={3}
                placeholder="Modifiez votre objectif original..."
                disabled={isGenerating}
              />
            </div>
            
            {isGenerating && (
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <div className="flex items-center">
                  <Loader2 className="h-4 w-4 animate-spin text-blue-600 mr-2" />
                  <p className="text-sm text-blue-700">
                    Régénération complète en cours (objectif, stratégie et plan d'action)...
                  </p>
                </div>
              </div>
            )}
            
            <div className="flex flex-col sm:flex-row justify-end gap-3">
              <button
                onClick={() => setIsEditing(false)}
                disabled={isGenerating}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 text-sm sm:text-base"
              >
                Annuler
              </button>
              <button
                onClick={handleSaveObjective}
                disabled={!tempObjective.trim() || isGenerating}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm sm:text-base flex items-center justify-center"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Mise à jour complète...
                  </>
                ) : (
                  'Sauvegarder et tout régénérer'
                )}
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white bg-opacity-70 p-3 sm:p-4 rounded-lg border border-gray-100">
            {/* Taille de police harmonisée avec la page Ambition */}
            <div className="mb-3">
              <p className="text-xs text-gray-500 mb-1">Votre texte original :</p>
              <p className="text-sm sm:text-base text-gray-800 font-medium break-words">
                {localCompetence.objectifOriginal || localCompetence.title}
              </p>
            </div>
            
            {/* Affichage du titre généré si différent */}
            {localCompetence.objectifOriginal && 
             localCompetence.objectifOriginal !== localCompetence.title && (
              <div className="mb-3 pt-2 border-t border-gray-200">
                <p className="text-xs text-gray-500 mb-1">Titre généré par l'IA :</p>
                <p className="text-sm sm:text-base text-gray-700 break-words">
                  {localCompetence.title}
                </p>
              </div>
            )}
            
            {/* Tags d'information */}
            <div className="flex flex-wrap gap-2 mt-3 pt-2 border-t border-gray-200">
              {localCompetence.type && (
                <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${
                  localCompetence.type === 'project' ? 'bg-purple-100 text-purple-700' :
                  localCompetence.type === 'idea' ? 'bg-yellow-100 text-yellow-700' :
                  localCompetence.type === 'desire' ? 'bg-pink-100 text-pink-700' :
                  localCompetence.type === 'skill' ? 'bg-green-100 text-green-700' :
                  localCompetence.type === 'career' ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {localCompetence.type === 'project' ? 'Projet' :
                   localCompetence.type === 'idea' ? 'Idée' :
                   localCompetence.type === 'desire' ? 'Désir' :
                   localCompetence.type === 'skill' ? 'Compétence' :
                   localCompetence.type === 'career' ? 'Carrière' : 'Objectif'}
                </span>
              )}
              
              {(localCompetence.chapitre || localCompetence.theme) && (
                <span className="px-2 sm:px-3 py-1 bg-blue-100 text-blue-700 text-xs sm:text-sm rounded-full break-words">
                  {localCompetence.chapitre || localCompetence.theme}
                </span>
              )}
              
              {localCompetence.lastAIUpdate && (
                <span className="px-2 sm:px-3 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                  Mis à jour par IA
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Recommandations - POLICE ET TAILLE HARMONISÉES */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4 sm:mb-6">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <Lightbulb className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 flex-shrink-0" />
            <h4 className="text-base sm:text-lg font-semibold text-gray-800 break-words">Stratégie recommandée</h4>
          </div>
          {isGenerating && (
            <div className="flex items-center text-xs sm:text-sm text-purple-600 flex-shrink-0">
              <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 animate-spin mr-1" />
              Génération en cours...
            </div>
          )}
        </div>

        {error && (
          <div className="mb-4 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm sm:text-base break-words">
            {error}
          </div>
        )}

        {/* Bouton pour générer seulement si pas de recommandation ET pas en génération */}
        {!recommendation && !isGenerating && (
          <div className="text-center py-6 sm:py-8">
            <Lightbulb className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-3 sm:mb-4" />
            <p className="text-gray-600 mb-3 sm:mb-4 text-sm sm:text-base">Aucune stratégie disponible</p>
            <button
              onClick={() => generateRecommendations(localCompetence.objectifOriginal || localCompetence.title)}
              className="px-4 py-2 sm:px-6 sm:py-3 bg-gradient-to-r from-purple-600 to-indigo-700 text-white rounded-lg hover:from-purple-700 hover:to-indigo-800 transition-colors flex items-center gap-2 mx-auto text-sm sm:text-base"
            >
              <Lightbulb className="w-4 h-4 sm:w-5 sm:h-5" />
              Générer une stratégie
            </button>
          </div>
        )}

        {isGenerating && !recommendation ? (
          <div className="space-y-4">
            <div className="animate-pulse">
              <div className="h-3 sm:h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-3 sm:h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-3 sm:h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
          </div>
        ) : sections ? (
          <div className="space-y-4 sm:space-y-6">
            {/* Section 1: Contexte - POLICE HARMONISÉE */}
            {sections.section1 && (
              <div className="bg-blue-50 p-3 sm:p-4 rounded-lg border border-blue-200">
                <h5 className="font-medium text-blue-800 mb-2 flex items-center text-sm sm:text-base">
                  <Target className="w-3 h-3 sm:w-4 sm:h-4 mr-2 flex-shrink-0" />
                  <span className="break-words">Contexte et vision</span>
                </h5>
                <div className="text-blue-700 prose prose-sm max-w-none text-xs sm:text-sm break-words">
                  <ReactMarkdown>{sections.section1}</ReactMarkdown>
                </div>
              </div>
            )}

            {/* Section 2: Démarche - POLICE HARMONISÉE */}
            {sections.section2 && (
              <div className="bg-green-50 p-3 sm:p-4 rounded-lg border border-green-200">
                <h5 className="font-medium text-green-800 mb-2 flex items-center text-sm sm:text-base">
                  <Compass className="w-3 h-3 sm:w-4 sm:h-4 mr-2 flex-shrink-0" />
                  <span className="break-words">Démarche recommandée</span>
                </h5>
                <div className="text-green-700 prose prose-sm max-w-none text-xs sm:text-sm break-words">
                  <ReactMarkdown>{sections.section2}</ReactMarkdown>
                </div>
              </div>
            )}

            {/* Section 3: Prochaines étapes - POLICE HARMONISÉE */}
            {sections.section3 && (
              <div className="bg-purple-50 p-3 sm:p-4 rounded-lg border border-purple-200">
                <h5 className="font-medium text-purple-800 mb-2 flex items-center text-sm sm:text-base">
                  <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 mr-2 flex-shrink-0" />
                  <span className="break-words">Prochaines étapes</span>
                </h5>
                <div className="text-purple-700 prose prose-sm max-w-none text-xs sm:text-sm break-words">
                  <ReactMarkdown>{sections.section3}</ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        ) : null}

        {/* Actions - AFFICHER SEULEMENT SI RECOMMANDATION EXISTE */}
        {recommendation && !isGenerating && (
          <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                onClick={onNavigateToAction}
                className="flex-1 px-4 py-2 sm:px-6 sm:py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-colors flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                <Zap className="w-4 h-4 sm:w-5 sm:h-5" />
                Passer au plan d'action
              </button>
              
              <button
                onClick={() => generateRecommendations(localCompetence.objectifOriginal || localCompetence.title)}
                className="px-4 py-2 sm:px-6 sm:py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">Nouvelles recommandations</span>
                <span className="sm:hidden">Régénérer</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

StrategyStep.displayName = 'StrategyStep';

// ============================================================================
// COMPOSANT PLAN D'ACTION COMPLET - VERSION RESPONSIVE (INCHANGÉ)
// ============================================================================

const ActionStep = React.memo(({ currentCompetence, userId, onNavigateToStrategy, onNavigateToStep, onUpdateObjective }) => {
  const [actionPlan, setActionPlan] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editableSteps, setEditableSteps] = useState([]);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Récupération systématique du plan d'action depuis Firebase
  const fetchActionPlan = useCallback(async () => {
    if (!currentCompetence?.id || !userId) return;
    
    setIsLoading(true);
    try {
      const docRef = doc(db, 'users', userId, 'competences', currentCompetence.id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.actionPlan) {
          setActionPlan(data.actionPlan);
          setEditableSteps(data.actionPlan.steps || []);
        } else {
          setActionPlan(currentCompetence.actionPlan || null);
          setEditableSteps(currentCompetence.actionPlan?.steps || []);
        }
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du plan d\'action:', error);
      setActionPlan(currentCompetence.actionPlan || null);
      setEditableSteps(currentCompetence.actionPlan?.steps || []);
    }
    setIsLoading(false);
  }, [currentCompetence, userId]);

  useEffect(() => {
    fetchActionPlan();
  }, [fetchActionPlan]);

  // Effet pour actualiser le plan d'action quand currentCompetence change
  useEffect(() => {
    if (currentCompetence?.actionPlan) {
      setActionPlan(currentCompetence.actionPlan);
      setEditableSteps(currentCompetence.actionPlan.steps || []);
    }
  }, [currentCompetence?.actionPlan]);

  // Générer un plan d'action initial
  const generateInitialPlan = async () => {
    if (!currentCompetence || !(currentCompetence.objectifOriginal || currentCompetence.title)) {
      setError('Informations insuffisantes pour générer un plan d\'action.');
      return;
    }
    
    setIsGenerating(true);
    setError(null);
    
    try {
      const objectiveText = currentCompetence.objectifOriginal || currentCompetence.title;
      const prompt = `En tant que coach spécialisé en développement personnel et professionnel, crée un plan d'action concret basé sur l'objectif suivant.

Objectif de l'utilisateur : "${objectiveText}"
${currentCompetence.description ? `Description : "${currentCompetence.description}"` : ''}
${currentCompetence.recommendation ? `Recommandations précédentes : "${currentCompetence.recommendation}"` : ''}

Génère un plan d'action avec 3 à 5 étapes claires et actionnables sous format JSON avec cette structure précise :
{
  "titre": "Plan d'action pour [résumé de l'objectif]",
  "description": "Une description brève du plan d'action qui explique comment il va aider l'utilisateur à atteindre son objectif",
  "steps": [
    {
      "id": 1,
      "titre": "Titre de l'étape 1",
      "description": "Description détaillée de l'étape 1 avec des actions concrètes",
      "duree": "Estimation de la durée en heures.",
      "termine": false
    }
  ]
}

Assure-toi que:
- Chaque étape soit spécifique, mesurable et réalisable
- Le format JSON soit strictement respecté pour faciliter le traitement
- Le contenu utilise des verbes d'action et encourage l'utilisateur
- Les étapes suivent une progression logique vers l'objectif final`;

      const response = await askGPT(prompt);
      
      // Extraction du JSON de la réponse
      let planData;
      try {
        const jsonMatch = response.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          planData = JSON.parse(jsonMatch[0]);
        } else {
          throw new Error('Format JSON non détecté dans la réponse');
        }
      } catch (jsonError) {
        console.error('Erreur de parsing JSON:', jsonError);
        throw new Error('Impossible de parser le plan d\'action');
      }
      
      // Mise à jour systématique en base de données
      const docRef = doc(db, 'users', userId, 'competences', currentCompetence.id);
      await updateDoc(docRef, {
        actionPlan: planData,
        actionPlanCreatedAt: new Date().toISOString(),
        actionPlanUpdatedAt: new Date().toISOString()
      });

      // Mise à jour de l'état local
      setActionPlan(planData);
      setEditableSteps(planData.steps || []);
      setFeedbackMessage('Plan d\'action généré avec succès !');
      
      // Notifier le parent de la mise à jour
      if (onUpdateObjective) {
        onUpdateObjective({
          ...currentCompetence,
          actionPlan: planData,
          actionPlanCreatedAt: new Date().toISOString(),
          actionPlanUpdatedAt: new Date().toISOString()
        });
      }
      
      setTimeout(() => {
        setFeedbackMessage('');
      }, 3000);
      
    } catch (err) {
      console.error('Erreur lors de la génération du plan:', err);
      setError('Erreur lors de la génération du plan d\'action');
    } finally {
      setIsGenerating(false);
    }
  };

  // Ajouter une nouvelle étape
  const addNewStep = () => {
    const newStep = {
      id: editableSteps.length > 0 ? Math.max(...editableSteps.map(step => step.id)) + 1 : 1,
      titre: "Nouvelle étape",
      description: "Description de l'étape",
      duree: "À déterminer",
      termine: false
    };
    
    setEditableSteps([...editableSteps, newStep]);
  };

  // Supprimer une étape
  const removeStep = (id) => {
    setEditableSteps(editableSteps.filter(step => step.id !== id));
  };

  // Mettre à jour une étape
  const updateStep = (id, field, value) => {
    setEditableSteps(editableSteps.map(step => 
      step.id === id ? { ...step, [field]: value } : step
    ));
  };

  // Sauvegarder avec mise à jour systématique
  const saveChanges = async () => {
    try {
      if (!actionPlan) return;
      
      const updatedPlan = {
        ...actionPlan,
        steps: editableSteps
      };
      
      const docRef = doc(db, 'users', userId, 'competences', currentCompetence.id);
      await updateDoc(docRef, {
        actionPlan: updatedPlan,
        actionPlanUpdatedAt: new Date().toISOString()
      });
      
      setActionPlan(updatedPlan);
      setIsEditing(false);
      setFeedbackMessage('Plan d\'action sauvegardé !');
      
      // Notifier le parent
      if (onUpdateObjective) {
        onUpdateObjective({
          ...currentCompetence,
          actionPlan: updatedPlan,
          actionPlanUpdatedAt: new Date().toISOString()
        });
      }
      
      setTimeout(() => {
        setFeedbackMessage('');
      }, 3000);
      
    } catch (err) {
      console.error('Erreur lors de la sauvegarde:', err);
      setError('Erreur lors de la sauvegarde du plan d\'action');
    }
  };

  // Marquer une étape comme terminée
  const toggleStepCompletion = async (id) => {
    try {
      const updatedSteps = actionPlan.steps.map(step => 
        step.id === id ? { ...step, termine: !step.termine } : step
      );
      
      const updatedPlan = {
        ...actionPlan,
        steps: updatedSteps
      };
      
      const docRef = doc(db, 'users', userId, 'competences', currentCompetence.id);
      await updateDoc(docRef, {
        actionPlan: updatedPlan,
        actionPlanUpdatedAt: new Date().toISOString()
      });
      
      setActionPlan(updatedPlan);
      setEditableSteps(updatedSteps);

      // Notifier le parent
      if (onUpdateObjective) {
        onUpdateObjective({
          ...currentCompetence,
          actionPlan: updatedPlan,
          actionPlanUpdatedAt: new Date().toISOString()
        });
      }
    } catch (err) {
      console.error('Erreur lors de la mise à jour:', err);
      setError('Erreur lors de la mise à jour du statut');
    }
  };

  // Modifier le titre ou la description du plan
  const updatePlanDetails = (field, value) => {
    setActionPlan(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Navigation vers le chat spécifique à cette compétence
  const handleNavigateToChat = () => {
    if (currentCompetence?.id) {
      window.location.href = `/chat/${currentCompetence.id}`;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 sm:py-12">
        <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600 text-sm sm:text-base">Chargement du plan d'action...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* En-tête du plan d'action - Responsive */}
      <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <Zap className="w-6 h-6 sm:w-8 sm:h-8 text-orange-600 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 break-words">Plan d'action</h3>
              <p className="text-xs sm:text-sm text-gray-600 break-words">Actions concrètes pour réaliser votre objectif</p>
            </div>
          </div>
          
          <button
            onClick={onNavigateToStrategy}
            className="px-3 py-2 sm:px-4 sm:py-2 text-blue-600 hover:text-blue-800 font-medium flex items-center gap-2 bg-white rounded-lg border border-blue-200 hover:bg-blue-50 transition-colors text-sm sm:text-base flex-shrink-0"
          >
            <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Retour à la stratégie</span>
            <span className="sm:hidden">Stratégie</span>
          </button>
        </div>

        <div className="bg-white bg-opacity-70 p-3 sm:p-4 rounded-lg border border-gray-100">
          <p className="text-base sm:text-lg text-gray-700 break-words">
            {currentCompetence.objectifOriginal || currentCompetence.title}
          </p>
          {(currentCompetence.theme || currentCompetence.chapitre) && (
            <span className="inline-block mt-2 px-2 sm:px-3 py-1 bg-orange-100 text-orange-700 text-xs sm:text-sm rounded-full break-words">
              {currentCompetence.chapitre || currentCompetence.theme}
            </span>
          )}
        </div>
      </div>

      {/* Contenu du plan d'action - Responsive */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6 lg:p-8">
        {feedbackMessage && (
          <div className="bg-green-50 border border-green-200 text-green-700 p-3 sm:p-4 rounded-lg mb-4 sm:mb-6 flex items-center text-sm sm:text-base">
            <CheckSquare className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0" />
            <p className="break-words">{feedbackMessage}</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 sm:p-4 rounded-lg mb-4 sm:mb-6 text-sm sm:text-base break-words">
            <p>{error}</p>
          </div>
        )}
        
        {!actionPlan && (
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 sm:p-6 text-center mb-4 sm:mb-6">
            <Lightbulb className="w-10 h-10 sm:w-12 sm:h-12 text-indigo-400 mx-auto mb-3 sm:mb-4" />
            <p className="text-gray-700 mb-4 sm:mb-6 text-base sm:text-lg break-words">
              Générez un plan d'action personnalisé basé sur votre objectif
            </p>
            <button
              onClick={generateInitialPlan}
              disabled={isGenerating}
              className={`px-4 py-2 sm:px-6 sm:py-3 rounded-lg flex items-center gap-2 transition-all duration-200 mx-auto text-sm sm:text-base
                ${isGenerating 
                  ? 'bg-gray-300 cursor-not-allowed text-gray-500' 
                  : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-md hover:shadow-lg'}`}
            >
              {isGenerating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-2 border-white border-t-transparent"></div>
                  <span>Génération en cours...</span>
                </>
              ) : (
                <>
                  <Lightbulb className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>Générer un plan d'action</span>
                </>
              )}
            </button>
          </div>
        )}
        
        {actionPlan && (
          <>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center break-words">
                <List className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-indigo-600 flex-shrink-0" />
                <span className="min-w-0">{isEditing ? "Modification du Plan d'Action" : "Votre Plan d'Action"}</span>
              </h2>
              
              {!isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-3 py-2 sm:px-4 sm:py-2 rounded-lg bg-white border border-indigo-300 text-indigo-700 hover:bg-indigo-50 flex items-center gap-2 transition-all duration-200 shadow-sm text-sm sm:text-base flex-shrink-0"
                >
                  <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Modifier les détails</span>
                  <span className="sm:hidden">Modifier</span>
                </button>
              )}
            </div>

            {/* Étapes responsives */}
            <div className="space-y-4 sm:space-y-6 mb-6 sm:mb-8">
              {(isEditing ? editableSteps : actionPlan.steps).map((step, index) => (
                <div key={step.id} className={`bg-white rounded-lg p-4 sm:p-6 shadow-sm border-2 transition-all overflow-hidden ${step.termine ? 'border-green-200 bg-green-50' : 'border-indigo-100 hover:border-indigo-200'}`}>
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                    <div className="flex items-start w-full min-w-0">
                      {!isEditing && (
                        <div 
                          onClick={() => toggleStepCompletion(step.id)}
                          className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full mr-3 sm:mr-4 flex items-center justify-center cursor-pointer transition-all flex-shrink-0 ${step.termine ? 'bg-green-500 text-white' : 'border-2 border-indigo-300 hover:border-indigo-500 hover:bg-indigo-50'}`}
                        >
                          {step.termine && <CheckSquare className="w-3 h-3 sm:w-5 sm:h-5" />}
                          {!step.termine && <span className="text-indigo-500 font-medium text-xs sm:text-base">{step.id}</span>}
                        </div>
                      )}
                      
                      <div className="flex-1 min-w-0">
                        {isEditing ? (
                          <input
                            type="text"
                            value={step.titre}
                            onChange={(e) => updateStep(step.id, 'titre', e.target.value)}
                            className="text-base sm:text-lg font-medium text-gray-800 p-2 border-b-2 border-indigo-200 focus:border-indigo-500 focus:outline-none bg-transparent w-full mb-2 break-words"
                          />
                        ) : (
                          <h4 className={`text-base sm:text-lg font-medium mb-2 break-words ${step.termine ? 'text-gray-500 line-through' : 'text-gray-800'}`}>
                            {step.titre}
                          </h4>
                        )}
                        
                        {isEditing ? (
                          <textarea
                            value={step.description}
                            onChange={(e) => updateStep(step.id, 'description', e.target.value)}
                            className="w-full p-3 border border-indigo-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent min-h-20 sm:min-h-24 mb-4 bg-white text-sm sm:text-base resize-none"
                          />
                        ) : (
                          <div className={`prose prose-sm sm:prose max-w-none mb-4 text-sm sm:text-base break-words ${step.termine ? 'text-gray-500' : 'text-gray-700'}`}>
                            <ReactMarkdown>{step.description}</ReactMarkdown>
                          </div>
                        )}
                        
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <div className="flex items-center text-xs sm:text-sm text-gray-500">
                            <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                            {isEditing ? (
                              <input
                                type="text"
                                value={step.duree}
                                onChange={(e) => updateStep(step.id, 'duree', e.target.value)}
                                className="p-1 border border-indigo-200 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white text-xs sm:text-sm min-w-0"
                              />
                            ) : (
                              <span className="break-words">{step.duree}</span>
                            )}
                          </div>
                          
                          {/* Actions pour chaque étape - Responsive */}
                          {!isEditing && !step.termine && (
                            <div className="flex flex-wrap gap-2">
                              <button
                                onClick={() => onNavigateToStep('inspiration')}
                                className="text-purple-600 hover:text-purple-800 flex items-center gap-1 text-xs sm:text-sm bg-purple-50 px-2 py-1 sm:px-3 sm:py-1 rounded-lg border border-purple-200 hover:bg-purple-100 transition-colors flex-shrink-0"
                                title="Rechercher des ressources pour cette étape"
                              >
                                <Search className="w-3 h-3 sm:w-4 sm:h-4" />
                                <span>Inspiration</span>
                              </button>
                              
                              <button
                                onClick={() => onNavigateToStep('exploration')}
                                className="text-teal-600 hover:text-teal-800 flex items-center gap-1 text-xs sm:text-sm bg-teal-50 px-2 py-1 sm:px-3 sm:py-1 rounded-lg border border-teal-200 hover:bg-teal-100 transition-colors flex-shrink-0"
                                title="Explorer la base de connaissances"
                              >
                                <GraduationCap className="w-3 h-3 sm:w-4 sm:h-4" />
                                <span className="hidden sm:inline">Exploration</span>
                                <span className="sm:hidden">Exploration</span>
                              </button>
                              
                              <button
                                onClick={handleNavigateToChat}
                                className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-xs sm:text-sm bg-blue-50 px-2 py-1 sm:px-3 sm:py-1 rounded-lg border border-blue-200 hover:bg-blue-100 transition-colors flex-shrink-0"
                                title="Discuter avec Aladinn sur cette étape"
                              >
                                <MessageCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                                <span>Chat</span>
                              </button>
                              
                              <button
                                onClick={() => toggleStepCompletion(step.id)}
                                className="text-green-600 hover:text-green-800 flex items-center gap-1 text-xs sm:text-sm bg-green-50 px-2 py-1 sm:px-3 sm:py-1 rounded-lg border border-green-200 hover:bg-green-100 transition-colors flex-shrink-0"
                              >
                                <CheckSquare className="w-3 h-3 sm:w-4 sm:h-4" />
                                <span>Terminer</span>
                              </button>
                            </div>
                          )}
                          
                          {!isEditing && step.termine && (
                            <div className="flex items-center text-green-600 text-xs sm:text-sm">
                              <CheckSquare className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                              <span>Terminé</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {isEditing && (
                      <button 
                        onClick={() => removeStep(step.id)}
                        className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 flex-shrink-0"
                      >
                        <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              
              {isEditing && (
                <button
                  onClick={addNewStep}
                  className="w-full py-3 sm:py-4 border-2 border-dashed border-indigo-300 rounded-lg text-indigo-600 hover:bg-indigo-50 flex items-center justify-center gap-2 transition-colors text-sm sm:text-base"
                >
                  <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>Ajouter une étape</span>
                </button>
              )}
            </div>

            {/* Description du plan - Responsive */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 sm:p-6 shadow-sm border border-indigo-100 mb-6 sm:mb-8">
              <div className="flex items-center gap-3 mb-3 sm:mb-4">
                <Target className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600 flex-shrink-0" />
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 break-words">À propos de ce plan</h3>
              </div>
              
              {isEditing ? (
                <>
                  <input
                    type="text"
                    value={actionPlan.titre}
                    onChange={(e) => updatePlanDetails('titre', e.target.value)}
                    className="w-full mb-3 sm:mb-4 p-2 sm:p-3 text-lg sm:text-xl font-semibold border border-indigo-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                    placeholder="Titre du plan d'action"
                  />
                  <textarea
                    value={actionPlan.description}
                    onChange={(e) => updatePlanDetails('description', e.target.value)}
                    className="w-full p-2 sm:p-3 border border-indigo-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent min-h-20 sm:min-h-24 bg-white text-sm sm:text-base resize-none"
                    placeholder="Description du plan d'action"
                  />
                </>
              ) : (
                <>
                  <h4 className="text-base sm:text-lg font-medium text-gray-800 mb-2 sm:mb-3 break-words">{actionPlan.titre}</h4>
                  <div className="prose prose-sm sm:prose max-w-none text-gray-700 text-sm sm:text-base break-words">
                    <ReactMarkdown>{actionPlan.description}</ReactMarkdown>
                  </div>
                </>
              )}
            </div>
            
            {/* Actions globales du plan - Responsive */}
            <div className="border-t border-gray-200 pt-4 sm:pt-6">
              {isEditing ? (
                <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4">
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setEditableSteps(actionPlan.steps);
                    }}
                    className="px-4 py-2 sm:px-6 sm:py-3 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 flex items-center justify-center gap-2 transition-colors text-sm sm:text-base"
                  >
                    <X className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span>Annuler</span>
                  </button>
                  <button
                    onClick={saveChanges}
                    className="px-4 py-2 sm:px-6 sm:py-3 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white flex items-center justify-center gap-2 transition-all duration-200 shadow-md hover:shadow-lg text-sm sm:text-base"
                  >
                    <Save className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span>Enregistrer les modifications</span>
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    <button
                      onClick={() => onNavigateToStep('inspiration')}
                      className="px-4 py-3 sm:px-6 sm:py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-colors flex items-center justify-center gap-2 sm:gap-3 shadow-md hover:shadow-lg text-sm sm:text-base"
                    >
                      <Search className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                      <div className="text-center min-w-0">
                        <div className="font-medium break-words">Rechercher l'inspiration</div>
                        <div className="text-xs sm:text-sm opacity-90 break-words">Ressources et guides</div>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => onNavigateToStep('exploration')}
                      className="px-4 py-3 sm:px-6 sm:py-4 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-lg hover:from-teal-700 hover:to-emerald-700 transition-colors flex items-center justify-center gap-2 sm:gap-3 shadow-md hover:shadow-lg text-sm sm:text-base"
                    >
                      <GraduationCap className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                      <div className="text-center min-w-0">
                        <div className="font-medium break-words">Explorer les connaissances</div>
                        <div className="text-xs sm:text-sm opacity-90 break-words">Base documentaire</div>
                      </div>
                    </button>
                    
                    <button
                      onClick={handleNavigateToChat}
                      className="px-4 py-3 sm:px-6 sm:py-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg hover:from-blue-700 hover:to-cyan-700 transition-colors flex items-center justify-center gap-2 sm:gap-3 shadow-md hover:shadow-lg text-sm sm:text-base sm:col-span-2 lg:col-span-1"
                    >
                      <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                      <div className="text-center min-w-0">
                        <div className="font-medium break-words">Discuter avec Aladinn</div>
                        <div className="text-xs sm:text-sm opacity-90 break-words">Support personnalisé</div>
                      </div>
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-3 sm:mt-4">
                    <button
                      onClick={generateInitialPlan}
                      disabled={isGenerating}
                      className="px-4 py-3 sm:px-6 sm:py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-colors flex items-center justify-center gap-2 sm:gap-3 shadow-md hover:shadow-lg disabled:opacity-50 text-sm sm:text-base"
                    >
                      <RefreshCw className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 ${isGenerating ? 'animate-spin' : ''}`} />
                      <div className="text-center min-w-0">
                        <div className="font-medium break-words">Nouveau plan</div>
                        <div className="text-xs sm:text-sm opacity-90 break-words">Régénérer</div>
                      </div>
                    </button>
                  </div>
                </>
              )}
            </div>
            
            {/* Statistiques du plan - Responsive */}
            {!isEditing && actionPlan.steps && (
              <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200">
                <div className="grid grid-cols-3 gap-3 sm:gap-4 text-center">
                  <div className="bg-blue-50 rounded-lg p-3 sm:p-4">
                    <div className="text-xl sm:text-2xl font-bold text-blue-600">{actionPlan.steps.length}</div>
                    <div className="text-xs sm:text-sm text-blue-700 break-words">Étapes au total</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 sm:p-4">
                    <div className="text-xl sm:text-2xl font-bold text-green-600">
                      {actionPlan.steps.filter(step => step.termine).length}
                    </div>
                    <div className="text-xs sm:text-sm text-green-700 break-words">Étapes terminées</div>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-3 sm:p-4">
                    <div className="text-xl sm:text-2xl font-bold text-orange-600">
                      {Math.round((actionPlan.steps.filter(step => step.termine).length / actionPlan.steps.length) * 100)}%
                    </div>
                    <div className="text-xs sm:text-sm text-orange-700 break-words">Progression</div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
});

ActionStep.displayName = 'ActionStep';

// ============================================================================
// MODULE PRINCIPAL STRATÉGIE + ACTION - Responsive et Complet
// ============================================================================

const StrategyActionModule = React.memo(({ 
  step, 
  currentStep,
  currentCompetence, 
  userId, 
  onStepChange,
  onUpdateObjective // AJOUT DU PROP POUR NOTIFIER LES MISES À JOUR
}) => {
  // Handlers pour la navigation entre stratégie et action
  const handleNavigateToAction = useCallback(() => {
    onStepChange('action');
  }, [onStepChange]);

  const handleNavigateToStrategy = useCallback(() => {
    onStepChange('strategy');
  }, [onStepChange]);

  // Rendu selon l'étape actuelle
  if (!currentCompetence) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="text-center mb-6 sm:mb-8">
          <step.icon className={`w-12 h-12 sm:w-16 sm:h-16 text-${step.color}-500 mx-auto mb-3 sm:mb-4`} />
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2 break-words">{step.title}</h2>
          <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base break-words px-4">{step.description}</p>
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 sm:p-8 text-center">
          <div className="mb-4 sm:mb-6">
            <AlertCircle className="w-12 h-12 sm:w-16 sm:h-16 text-amber-400 mx-auto mb-3 sm:mb-4" />
            <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2 break-words">Objectif requis</h3>
            <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base break-words">Vous devez d'abord sélectionner ou créer un objectif</p>
          </div>
          <button
            onClick={() => onStepChange('ambition')}
            className="px-4 py-2 sm:px-6 sm:py-3 bg-gradient-to-r from-purple-500 to-blue-600 text-white rounded-lg hover:from-purple-600 hover:to-blue-700 transition-colors text-sm sm:text-base"
          >
            Définir mon ambition
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Navigation entre stratégie et action - Responsive */}
      <div className="flex justify-center mb-6 sm:mb-8">
        <div className="bg-white rounded-lg p-1 border border-gray-200 shadow-sm flex w-full max-w-md">
          <button
            onClick={() => onStepChange('strategy')}
            className={`flex-1 px-4 py-2 sm:px-6 sm:py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 text-sm sm:text-base ${
              currentStep === 'strategy' 
                ? 'bg-blue-500 text-white shadow-sm' 
                : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
            }`}
          >
            <Compass className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>Stratégie</span>
          </button>
          
          <button
            onClick={() => onStepChange('action')}
            className={`flex-1 px-4 py-2 sm:px-6 sm:py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 text-sm sm:text-base ${
              currentStep === 'action' 
                ? 'bg-orange-500 text-white shadow-sm' 
                : 'text-gray-600 hover:text-orange-600 hover:bg-orange-50'
            }`}
          >
            <Zap className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="hidden sm:inline">Plan d'action</span>
            <span className="sm:hidden">Action</span>
          </button>
        </div>
      </div>

      {/* Contenu selon l'étape */}
      {currentStep === 'strategy' && (
        <StrategyStep
          currentCompetence={currentCompetence}
          userId={userId}
          onNavigateToAction={handleNavigateToAction}
          onUpdateObjective={onUpdateObjective} // TRANSMISSION DU PROP
        />
      )}

      {currentStep === 'action' && (
        <ActionStep
          currentCompetence={currentCompetence}
          userId={userId}
          onNavigateToStrategy={handleNavigateToStrategy}
          onNavigateToStep={onStepChange}
          onUpdateObjective={onUpdateObjective} // TRANSMISSION DU PROP
        />
      )}
    </div>
  );
}); 
 
StrategyActionModule.displayName = 'StrategyActionModule';

export default StrategyActionModule;;
